import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { resolveAuthOrganizationByEmail } from "@/app/_services/Auth/api";
import { getEmailDomain, normalizeEmail } from "@/app/_utils/Auth/email";

type LoginOptions = {
  forceLogin?: boolean;
};

export function useLogin() {
  const t = useTranslations("auth");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loginWithAuth0 = async (
    email?: string,
    _options?: LoginOptions
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const trimmedEmail = email?.trim() ?? "";
      if (!trimmedEmail) {
        setError(t("emailRequired"));
        setIsLoading(false);
        return;
      }
      if (!getEmailDomain(trimmedEmail)) {
        setError(t("emailInvalid"));
        setIsLoading(false);
        return;
      }

      let auth0OrgId: string | undefined;
      try {
        const resolved = await resolveAuthOrganizationByEmail(trimmedEmail);
        auth0OrgId = resolved.auth0OrgId;
      } catch {
        setError(t("organizationDomainNotFound"));
        setIsLoading(false);
        return;
      }

      const authorizationParams: Record<string, string> = {};
      if (auth0OrgId) {
        authorizationParams.organization = auth0OrgId;
      }
      authorizationParams.login_hint = normalizeEmail(trimmedEmail);
      authorizationParams.prompt = "login";
      authorizationParams.max_age = "0";

      await signIn(
        "auth0",
        { redirect: true, callbackUrl: "/" },
        authorizationParams
      );
    } catch {
      setError(t("unknownError"));
      setIsLoading(false);
    }
  };

  return {
    loginWithAuth0,
    isLoading,
    error,
  };
}
